/**
 * Pipeline configuration schema and parsing
 *
 * This module provides the canonical schema for .kiln.yml files,
 * used by both master (for trigger matching) and worker (for execution).
 */
import { z } from 'zod';
import { parse as parseYaml } from 'yaml';
// ============================================
// Environment Variables
// ============================================
// Schema for environment variable values (string, number, or boolean -> converted to string)
export const EnvValueSchema = z.union([z.string(), z.number(), z.boolean()]).transform(String);
// ============================================
// Commands
// ============================================
// Non-empty command string
export const CommandSchema = z.string().refine((cmd) => cmd.trim().length > 0, {
    message: 'Command cannot be empty',
});
// ============================================
// Stage When Conditions (for conditional stage execution)
// ============================================
// Single when condition object (AND logic within)
export const WhenConditionObjectSchema = z.object({
    // Run only on specific branches (string or array of strings, supports glob patterns)
    branch: z.union([z.string(), z.array(z.string())]).optional(),
    // Tag condition: true = run only on tags, false = run only on non-tags, undefined = run on both
    tag: z.boolean().optional(),
});
// When condition: object (AND logic) or array of objects (OR logic)
// - Object syntax: { branch: 'main', tag: false } - all conditions must match
// - Array syntax: [{ branch: 'main' }, { tag: true }] - any condition matches = run
export const WhenConditionSchema = z.union([
    WhenConditionObjectSchema,
    z.array(WhenConditionObjectSchema),
]).optional();
// ============================================
// Override Conditions (for env overrides)
// ============================================
export const OverrideConditionSchema = z.object({
    // Match by ref type: branch, tag, or pull_request
    ref_type: z.enum(['branch', 'tag', 'pull_request']).optional(),
    // Match by branch name pattern (supports glob patterns)
    branch: z.string().optional(),
    // Match by tag name pattern (supports glob patterns)
    tag: z.string().optional(),
});
export const OverrideSchema = z.object({
    when: OverrideConditionSchema,
    env: z.record(z.string(), EnvValueSchema).optional(),
});
// ============================================
// Artifacts
// ============================================
// When to collect stage artifacts
export const ArtifactWhenSchema = z.enum(['on_success', 'always', 'on_failure']).default('on_success');
// Stage artifact definition schema (inline in pipeline config)
export const StageArtifactSchema = z.object({
    name: z.string().min(1, 'Artifact name is required'),
    globs: z.array(z.string().min(1)).min(1, 'At least one glob pattern is required'),
    when: ArtifactWhenSchema.optional(), // When to collect: on_success (default), always, on_failure
});
// ============================================
// Stages
// ============================================
export const StageSchema = z.object({
    name: z.string().min(1, 'Stage name is required'),
    commands: z.array(CommandSchema).min(1, 'At least one command is required per stage'),
    when: WhenConditionSchema,
    artifacts: z.array(StageArtifactSchema).optional(),
});
// ============================================
// Pipeline Triggers (on: clause)
// ============================================
/**
 * Branch filter for push and pull_request triggers
 *
 * Supports:
 * - branches: array of patterns to include
 * - branches-ignore: array of patterns to exclude
 */
export const BranchFilterSchema = z.object({
    branches: z.array(z.string()).optional(),
    'branches-ignore': z.array(z.string()).optional(),
});
/**
 * Tag filter for tag triggers
 *
 * Supports:
 * - tags: array of patterns to include
 * - tags-ignore: array of patterns to exclude
 */
export const TagFilterSchema = z.object({
    tags: z.array(z.string()).optional(),
    'tags-ignore': z.array(z.string()).optional(),
});
/**
 * Push trigger schema
 *
 * Can be:
 * - true or null: trigger on all branches
 * - object with filters: trigger on matching branches
 */
export const PushTriggerSchema = z.union([
    z.literal(true),
    z.null(),
    BranchFilterSchema,
]).optional();
/**
 * Pull request trigger schema
 *
 * Can be:
 * - true or null: trigger on all PRs
 * - object with filters: trigger on PRs targeting matching branches
 */
export const PullRequestTriggerSchema = z.union([
    z.literal(true),
    z.null(),
    BranchFilterSchema,
]).optional();
/**
 * Tag trigger schema
 *
 * Can be:
 * - true or null: trigger on all tags
 * - object with filters: trigger on matching tags
 */
export const TagTriggerSchema = z.union([
    z.literal(true),
    z.null(),
    TagFilterSchema,
]).optional();
/**
 * Schedule trigger entry (a single cron expression)
 */
export const ConcurrencyPolicySchema = z.enum(['replace', 'skip', 'queue']);
export const ScheduleEntrySchema = z.object({
    cron: z.string().min(1, 'Cron expression is required'),
    branch: z.string().optional(),
    concurrency: ConcurrencyPolicySchema.optional(),
});
/**
 * Schedule trigger schema - array of cron entries
 *
 * Example:
 *   schedule:
 *     - cron: "0 2 * * *"       # Every night at 2am UTC
 *     - cron: "0 12 * * 1"      # Every Monday at noon UTC
 */
export const ScheduleTriggerSchema = z.array(ScheduleEntrySchema).min(1);
/**
 * Single pipeline trigger configuration object
 *
 * All fields are optional - omitting a trigger type means it won't fire for that event.
 * Multiple trigger types in the same object are OR logic - the pipeline runs
 * if any trigger matches the event.
 */
export const PipelineTriggerObjectSchema = z.object({
    push: PushTriggerSchema,
    pull_request: PullRequestTriggerSchema,
    tag: TagTriggerSchema,
    schedule: ScheduleTriggerSchema.optional(),
}).partial();
/**
 * Pipeline trigger configuration
 *
 * Uses object syntax where multiple trigger types are OR logic - the pipeline runs
 * if any trigger matches the event.
 *
 * Example: `on: { push: { branches: [main] }, tag: true }`
 * This runs on pushes to main OR on any tag.
 */
export const PipelineOnSchema = PipelineTriggerObjectSchema.optional();
// ============================================
// Pipeline
// ============================================
/**
 * Pipeline schema
 *
 * A pipeline defines:
 * - name: unique identifier
 * - on: trigger configuration (when to run)
 * - stages: ordered list of stages to execute
 * - finally: stages that always run (even on failure)
 */
/**
 * Label constraints attached to a pipeline in `.kiln.yml`.
 *
 *   labels:
 *     requires: { opcli: "true", arch: arm64 }   # hard: build only runs
 *                                                # on workers whose
 *                                                # `labels` contain every
 *                                                # key/value here.
 *     prefers:  { region: eu-west-1 }            # soft: ranking boost
 *                                                # when a worker matches.
 *                                                # Never gates eligibility.
 *
 * Values are plain strings — Kubernetes-style matchExpressions are not
 * supported.
 */
export const PipelineLabelsSchema = z.object({
    requires: z.record(z.string(), z.string()).optional(),
    prefers: z.record(z.string(), z.string()).optional(),
});
export const PipelineSchema = z.object({
    name: z.string().min(1, 'Pipeline name is required'),
    on: PipelineOnSchema,
    stages: z.array(StageSchema).min(1, 'At least one stage is required'),
    // Finally stages always run, even if main stages fail
    finally: z.array(StageSchema).optional(),
    // Docker image used to run every command in this pipeline.
    // When omitted, the executor's default image is used (see
    // `DockerExecExecutor.defaultImage` on the agent). Ignored by
    // `LocalBashExecutor` (legacy worker) which runs commands on the host.
    image: z.string().min(1).optional(),
    // Worker-routing constraints stamped into every build this pipeline
    // produces. See PipelineLabelsSchema.
    labels: PipelineLabelsSchema.optional(),
});
// ============================================
// Full Pipeline Config
// ============================================
/**
 * Complete .kiln.yml configuration schema
 */
export const PipelineConfigSchema = z.object({
    version: z.literal(1, {
        errorMap: () => ({ message: 'Only version 1 is supported' }),
    }),
    env: z.record(z.string(), EnvValueSchema).optional(),
    overrides: z.array(OverrideSchema).optional(),
    pipelines: z.array(PipelineSchema).min(1, "At least one pipeline is required. Note: The shortcut 'commands' syntax is no longer supported - use 'pipelines' instead."),
});
// ============================================
// Parsing Functions
// ============================================
/**
 * Format Zod validation errors into a readable message
 */
function formatZodError(error) {
    const issues = error.issues.map((issue) => {
        const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}${issue.message}`;
    });
    return issues.join('; ');
}
/**
 * Parse and validate a .kiln.yml configuration
 *
 * This is the canonical parser - use this in both master and worker
 * to ensure consistent validation.
 */
export function parsePipelineConfig(content) {
    let parsed;
    try {
        parsed = parseYaml(content);
    }
    catch (err) {
        return {
            success: false,
            error: `Invalid YAML syntax: ${err instanceof Error ? err.message : String(err)}`,
        };
    }
    // Validate with Zod
    const result = PipelineConfigSchema.safeParse(parsed);
    if (!result.success) {
        return {
            success: false,
            error: formatZodError(result.error),
        };
    }
    return {
        success: true,
        config: {
            version: result.data.version,
            env: result.data.env,
            overrides: result.data.overrides,
            pipelines: result.data.pipelines,
        },
    };
}
/**
 * Simple glob-like pattern matching
 * Supports * for any characters and ** for any path segments
 */
export function matchesPattern(value, pattern) {
    // Convert glob pattern to regex
    const regexPattern = pattern
        .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
        .replace(/\*\*/g, '{{GLOBSTAR}}') // Temporarily replace **
        .replace(/\*/g, '[^/]*') // * matches any chars except /
        .replace(/{{GLOBSTAR}}/g, '.*'); // ** matches anything including /
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(value);
}
/**
 * Evaluate whether an override condition matches the build context
 */
export function matchesOverrideCondition(condition, context) {
    // Check ref_type condition
    if (condition.ref_type) {
        const actualRefType = context.isTag
            ? 'tag'
            : context.pullRequestId
                ? 'pull_request'
                : 'branch';
        if (condition.ref_type !== actualRefType) {
            return false;
        }
    }
    // Check branch pattern (simple glob-like matching with *)
    if (condition.branch) {
        if (!matchesPattern(context.branch, condition.branch)) {
            return false;
        }
    }
    // Check tag pattern (only applies if this is a tag build)
    if (condition.tag) {
        if (!context.isTag) {
            return false;
        }
        if (!matchesPattern(context.branch, condition.tag)) {
            return false;
        }
    }
    return true;
}
/**
 * Interpolate environment variable references in a string
 * Supports ${VAR_NAME} syntax
 */
export function interpolateEnvValue(value, env) {
    return value.replace(/\$\{([A-Z_][A-Z0-9_]*)\}/g, (_, varName) => {
        return env[varName] ?? '';
    });
}
/**
 * Apply overrides to the base environment based on build context
 * Overrides are applied in order, later overrides take precedence
 *
 * @param baseEnv - The base environment variables (from .kiln.yml env section)
 * @param overrides - The override definitions from .kiln.yml
 * @param context - The build context for evaluating conditions
 * @param systemEnv - System environment variables (KILN_*) for interpolation
 * @param log - Optional logging function for debugging
 * @returns Merged environment with applicable overrides applied
 */
export function applyOverrides(baseEnv, overrides, context, systemEnv, log) {
    if (!overrides || overrides.length === 0) {
        return baseEnv;
    }
    // Start with base env
    const result = { ...baseEnv };
    // Combined env for interpolation (system vars + current result)
    const getInterpolationEnv = () => ({ ...systemEnv, ...result });
    // Apply each matching override in order
    for (let i = 0; i < overrides.length; i++) {
        const override = overrides[i];
        const conditionStr = JSON.stringify(override.when);
        const matches = matchesOverrideCondition(override.when, context);
        log?.(`Override ${i + 1}: when=${conditionStr} => ${matches ? 'MATCHED' : 'no match'}`);
        if (matches) {
            // Apply this override's env vars
            if (override.env) {
                for (const [key, value] of Object.entries(override.env)) {
                    // Interpolate any ${VAR} references
                    const interpolated = interpolateEnvValue(value, getInterpolationEnv());
                    log?.(`  Setting ${key}="${interpolated}" (from "${value}")`);
                    result[key] = interpolated;
                }
            }
        }
    }
    return result;
}
/**
 * Evaluate whether a single condition object matches the build context (AND logic)
 */
function matchesSingleWhenCondition(condition, context) {
    // Check tag condition: true = only tags, false = only non-tags, undefined = both
    if (condition.tag === true && !context.isTag) {
        return false;
    }
    if (condition.tag === false && context.isTag) {
        return false;
    }
    // Check branch condition
    if (condition.branch) {
        const allowedBranches = Array.isArray(condition.branch) ? condition.branch : [condition.branch];
        const matches = allowedBranches.some(pattern => matchesPattern(context.branch, pattern));
        if (!matches) {
            return false;
        }
    }
    return true;
}
/**
 * Evaluate whether a stage should run based on its `when` condition
 *
 * Supports two syntaxes:
 * - Object: { branch: 'main', tag: false } - AND logic, all conditions must match
 * - Array: [{ branch: 'main' }, { tag: true }] - OR logic, any condition matches = run
 */
export function shouldRunStage(stage, context) {
    const when = stage.when;
    // No condition means always run
    if (!when) {
        return true;
    }
    // Array = OR logic (any condition matches)
    if (Array.isArray(when)) {
        return when.some(cond => matchesSingleWhenCondition(cond, context));
    }
    // Object = AND logic (all conditions must match)
    return matchesSingleWhenCondition(when, context);
}
// ============================================
// Constants
// ============================================
export const PIPELINE_FILENAME = '.kiln.yml';
export function getPipelineFilename() {
    return PIPELINE_FILENAME;
}
//# sourceMappingURL=index.js.map